import React, { useEffect, useState } from "react";
import { useNode, useEditor } from "@craftjs/core";
import { Box, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { useTextPersistence } from "qwanyx";

import { createEditorConfig } from './utils/editorConfig';
import { injectStyles } from './utils/styleInjection';
import { useRichTextEditor } from './hooks/useRichTextEditor';
import { useDisplayContent } from './hooks/useDisplayContent';
import { ToolbarPlugin } from './plugins/ToolbarPlugin';
import { UpdateHandler } from './plugins/UpdateHandler';
import { InitializeEditorPlugin } from './plugins/InitializeEditorPlugin';
import { RichTextSettings } from './RichTextSettings';

export const RichText = ({
  name,
  text = "Rich text content",
  lexicalState = "",
  qwantumId = null,
  onSave,
  mode = "question",
  alwaysShowEditor = false,
  showModeButtons = false,
  toolbarConfig = {},
  fontSize = 16,
  fontFamily = 'Arial',
  fontWeight = 'normal',
  fontStyle = 'normal',
  textAlign = 'left',
  color = '#000000',
  width = '100%',
  height = 'auto',
  minHeight = '60px',
  style,
  ...props
}) => {
  const { 
    connectors: { connect, drag }, 
    hasSelectedNode, 
    actions: { setProp } 
  } = useNode((state) => ({
    hasSelectedNode: state.events.selected,
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  // Use persistence hook when qwantumId is provided
  const { text: persistedText, handleBlur: persistenceBlur } = useTextPersistence(qwantumId, onSave);
  const [prevLexicalState, setPrevLexicalState] = useState("");

  // Initialize prevLexicalState properly
  useEffect(() => {
    if (qwantumId) {
      const initialState = persistedText || lexicalState || "";
      setPrevLexicalState(initialState);
    }
  }, [qwantumId, persistedText, lexicalState]);

  const { editable, setEditable } = useRichTextEditor({ 
    hasSelectedNode, 
    alwaysShowEditor 
  });

  // Get the current state to use (persisted or local)
  const currentLexicalState = qwantumId ? (persistedText || lexicalState) : lexicalState;

  const { getCurrentDisplayContent, hasRedacted } = useDisplayContent({
    mode,
    lexicalState: currentLexicalState,
    text,
    editable
  });

  useEffect(() => {
    injectStyles();
  }, []);

  const initialConfig = createEditorConfig();

  // Merge showModeButtons into toolbarConfig for backward compatibility
  const finalToolbarConfig = {
    showModeButtons,
    ...toolbarConfig
  };

  const handleContentChange = (textContent, newLexicalState) => {
    if (qwantumId) {
      // When using persistence, only update if state actually changed
      if (newLexicalState !== prevLexicalState) {
        setPrevLexicalState(newLexicalState);
        // The persistence will be handled by the blur event
      }
    } else {
      // Normal Craft.js prop update
      setProp(props => {
        props.text = textContent;
        props.lexicalState = newLexicalState;
      });
    }
  };

  const handleBlur = () => {
    console.log('HandleBlur called', { qwantumId, prevLexicalState, persistedText, lexicalState });
    
    if (qwantumId && prevLexicalState !== (persistedText || lexicalState)) {
      console.log('Persisting to database:', prevLexicalState);
      
      // Create a fake event for the persistence hook
      const fakeEvent = {
        target: { value: prevLexicalState }
      };
      persistenceBlur(fakeEvent);
      
      if (onSave) {
        onSave(prevLexicalState);
      }
    }
  };

  const handleModeChange = (newMode) => {
    setProp(props => props.mode = newMode);
  };

  const getTextStyle = () => ({
    fontSize: `${fontSize}px`,
    fontFamily: fontFamily,
    fontWeight: fontWeight,
    fontStyle: fontStyle,
    textAlign: textAlign,
    color: color,
    outline: 'none',
    margin: 0,
    padding: 0,
    minHeight: '20px'
  });

  return (
    <div 
      ref={ref => connect(drag(ref))}
      name={name}
      onClick={enabled && !alwaysShowEditor && !editable ? () => setEditable(true) : undefined}
      style={{ 
        border: 'none',
        height: height === 'auto' ? 'auto' : `calc(${height} + ${editable ? '60px' : '0px'})`,
        width: width,
        display: 'flex',
        flexDirection: 'column',
        ...style 
      }}
      {...props}
    >
      {hasRedacted && showModeButtons && !editable && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          marginBottom: 1,
          padding: 1,
          backgroundColor: '#f9f9f9',
          borderRadius: 1,
          border: '1px solid #ddd'
        }}>
          <ToggleButtonGroup
            value={mode}
            exclusive
            onChange={(e, value) => value && handleModeChange(value)}
            size="small"
          >
            <ToggleButton value="question">Question</ToggleButton>
            <ToggleButton value="answer">Answer</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ fontSize: '12px', color: '#666', marginLeft: 1 }}>
            {mode === 'question' ? 'Showing input fields for blanks' : 'Showing original redacted text'}
          </Box>
        </Box>
      )}

      <LexicalComposer initialConfig={initialConfig}>
        {editable && (
          <div style={{ flexShrink: 0 }}>
            <ToolbarPlugin 
              mode={mode} 
              onModeChange={handleModeChange} 
              toolbarConfig={finalToolbarConfig}
            />
          </div>
        )}
        
        <div 
          style={{
            height: height === 'auto' ? 'auto' : height,
            minHeight: height === 'auto' ? minHeight : undefined,
            overflow: height === 'auto' ? 'visible' : 'auto',
            flex: height === 'auto' ? '0 0 auto' : 'none',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <RichTextPlugin
            contentEditable={
              <ContentEditable 
                style={{
                  outline: 'none',
                  width: '100%',
                  height: '100%',
                  flex: 1,
                  padding: '8px',
                  ...getTextStyle(),
                  border: editable ? '1px solid #ccc' : 'none',
                  borderRadius: editable ? '4px' : '0px',
                  background: editable ? 'white' : 'transparent',
                  direction: 'ltr',
                  unicodeBidi: 'normal',
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  display: 'block',
                  writingMode: 'horizontal-tb',
                  textOrientation: 'mixed',
                  overflow: height === 'auto' ? 'visible' : 'auto',
                  cursor: !editable && enabled && !alwaysShowEditor ? 'pointer' : 'text',
                  boxSizing: 'border-box'
                }}
                readOnly={!editable}
                onBlur={handleBlur}
              />
            }
            placeholder={
              editable && !alwaysShowEditor ? (
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  left: '16px',
                  color: '#999',
                  pointerEvents: 'none',
                  ...getTextStyle(),
                }}>
                  Enter rich text...
                </div>
              ) : null
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        
        <InitializeEditorPlugin lexicalState={currentLexicalState} />
        <UpdateHandler onChange={handleContentChange} onBlur={handleBlur} qwantumId={qwantumId} />
        <HistoryPlugin />
        {editable && <AutoFocusPlugin />}
        <ListPlugin />
        <LinkPlugin />
      </LexicalComposer>
    </div>
  );
};

RichText.craft = {
  props: {
    name: "",
    text: "Rich text content",
    lexicalState: "",
    qwantumId: null,
    mode: "question",
    alwaysShowEditor: false,
    showModeButtons: true,
    toolbarConfig: {},
    fontSize: 16,
    fontFamily: 'Arial',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textAlign: 'left',
    color: '#000000',
    width: '100%',
    height: 'auto',
    minHeight: '60px'
  },
  rules: {
    canDrag: (node) => node.data.props.text !== "Drag"
  },
  related: {
    settings: RichTextSettings
  }
};